import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  RefreshCw, 
  Home, 
  ChevronDown, 
  ChevronUp,
  Wifi,
  WifiOff,
  Shield,
  Clock,
  XCircle,
  CheckCircle2,
  Copy,
  X
} from 'lucide-react';
import Icon from '../../../components/AppIcon';


// Reusable Error State Components
const ErrorState = ({ 
  message = 'Something went wrong', 
  onRetry, 
  showDetails = false,
  technicalDetails = null,
  variant = 'error'
}) => {
  const [showTechnical, setShowTechnical] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyError = () => {
    const errorText = JSON.stringify({
      message,
      details: technicalDetails,
      timestamp: new Date()?.toISOString()
    }, null, 2);
    
    navigator.clipboard?.writeText(errorText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const variantConfig = {
    error: {
      bgColor: 'bg-error-50',
      borderColor: 'border-error-600',
      textColor: 'text-error-600',
      icon: AlertTriangle,
      iconColor: 'text-error-600'
    },
    warning: {
      bgColor: 'bg-warning-50',
      borderColor: 'border-primary',
      textColor: 'text-primary',
      icon: AlertTriangle,
      iconColor: 'text-primary'
    },
    network: {
      bgColor: 'bg-error-50',
      borderColor: 'border-error-600',
      textColor: 'text-error-600',
      icon: WifiOff,
      iconColor: 'text-error-600'
    }
  };

  const config = variantConfig?.[variant] || variantConfig?.error;
  const Icon = config?.icon;

  return (
    <div 
      className={`${config?.bgColor} border-l-4 ${config?.borderColor} p-6 rounded-sm shadow-md`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-4">
        <Icon className={`w-6 h-6 ${config?.iconColor} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 space-y-3">
          <p className={`font-semibold ${config?.textColor} text-lg`}>{message}</p>
          
          {showDetails && technicalDetails && (
            <div className="mt-3">
              <button
                onClick={() => setShowTechnical(!showTechnical)}
                className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {showTechnical ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                <span>{showTechnical ? 'Hide' : 'Show'} Technical Details</span>
              </button>
              
              {showTechnical && (
                <div className="mt-3 p-4 bg-surface border border-border rounded-sm">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-xs font-medium text-text-secondary">Error Details:</p>
                    <button
                      onClick={handleCopyError}
                      className="text-xs text-primary hover:text-primary-600 flex items-center gap-1"
                      title="Copy error details"
                    >
                      {copied ? (
                        <>
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <pre className="text-xs text-text-primary overflow-auto max-h-48 bg-background p-3 rounded">
                    {JSON.stringify(technicalDetails, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
          
          {onRetry && (
            <button
              onClick={onRetry}
              className="btn-primary px-5 py-2 text-sm flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ 
  title = 'No data available', 
  description = 'There are no items to display', 
  action = null,
  illustration = null
}) => {
  return (
    <div className="text-center py-12 px-4">
      {illustration || (
        <div className="mx-auto w-24 h-24 mb-6 flex items-center justify-center">
          <svg viewBox="0 0 100 100" className="w-full h-full text-gray-300">
            <circle cx="50" cy="50" r="40" fill="currentColor" opacity="0.2" />
            <path
              d="M50 30 L50 55 M50 65 L50 70"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}
      <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>
      <p className="text-sm text-text-secondary mb-6 max-w-md mx-auto">{description}</p>
      {action && <div className="flex justify-center">{action}</div>}
    </div>
  );
};

const NetworkErrorState = ({ onRetry, isOffline = true }) => {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    await onRetry?.();
    setTimeout(() => setRetrying(false), 2000);
  };

  return (
    <div className="card p-8 text-center max-w-lg mx-auto">
      <div className="flex justify-center mb-4">
        <div className={`p-4 rounded-full ${isOffline ? 'bg-error-100' : 'bg-warning-100'}`}>
          {isOffline ? (
            <WifiOff className="w-12 h-12 text-error-600" />
          ) : (
            <Wifi className="w-12 h-12 text-primary" />
          )}
        </div>
      </div>
      <h3 className="text-xl font-semibold text-text-primary mb-2">
        {isOffline ? 'No Internet Connection' : 'Connection Issues'}
      </h3>
      <p className="text-sm text-text-secondary mb-6">
        {isOffline 
          ? 'Please check your network connection and try again' :'We\'re having trouble connecting. This might be temporary.'}
      </p>
      <button
        onClick={handleRetry}
        disabled={retrying}
        className="btn-secondary px-6 py-2.5 inline-flex items-center gap-2"
      >
        {retrying ? (
          <>
            <div className="spinner-green w-4 h-4" />
            <span>Reconnecting...</span>
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4" />
            <span>Try Reconnecting</span>
          </>
        )}
      </button>
    </div>
  );
};

const PermissionErrorState = ({ requiredRole = 'Administrator', contactEmail = 'admin@example.com' }) => {
  return (
    <div className="card p-8 text-center max-w-lg mx-auto">
      <div className="flex justify-center mb-4">
        <div className="p-4 rounded-full bg-warning-100">
          <Shield className="w-12 h-12 text-primary" />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-text-primary mb-2">Access Denied</h3>
      <p className="text-sm text-text-secondary mb-6">
        You don't have permission to access this resource. 
        This action requires <span className="font-semibold text-primary">{requiredRole}</span> role.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => window.history?.back()}
          className="btn-outline px-5 py-2 text-sm"
        >
          Go Back
        </button>
        <a
          href={`mailto:${contactEmail}`}
          className="btn-primary px-5 py-2 text-sm inline-flex items-center gap-2"
        >
          Contact Administrator
        </a>
      </div>
    </div>
  );
};

const TimeoutErrorState = ({ onRetry, onExtendWait }) => {
  return (
    <div className="card p-8 text-center max-w-lg mx-auto">
      <div className="flex justify-center mb-4">
        <div className="p-4 rounded-full bg-warning-100">
          <Clock className="w-12 h-12 text-primary" />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-text-primary mb-2">Request Timeout</h3>
      <p className="text-sm text-text-secondary mb-6">
        This is taking longer than expected. You can retry now or wait a bit longer.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onRetry}
          className="btn-primary px-5 py-2 text-sm inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Retry Now
        </button>
        <button
          onClick={onExtendWait}
          className="btn-outline px-5 py-2 text-sm"
        >
          Wait Longer
        </button>
      </div>
    </div>
  );
};

const Toast = ({ message, type = 'error', onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const typeConfig = {
    error: { bg: 'bg-error-600', icon: XCircle },
    success: { bg: 'bg-secondary', icon: CheckCircle2 },
    warning: { bg: 'bg-primary', icon: AlertTriangle }
  };

  const config = typeConfig?.[type] || typeConfig?.error;
  const Icon = config?.icon;

  return (
    <div 
      className={`fixed bottom-6 right-6 ${config?.bg} text-white px-6 py-4 rounded-sm shadow-xl max-w-md transition-all duration-300 z-50 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <p className="flex-1 text-sm font-medium">{message}</p>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 hover:opacity-80 transition-opacity"
          aria-label="Close notification"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const NotFoundError = () => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="mb-8">
          <h1 className="text-9xl font-bold text-primary mb-2">404</h1>
          <div className="w-32 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full" />
        </div>
        <h2 className="text-2xl font-semibold text-text-primary mb-3">Page Not Found</h2>
        <p className="text-text-secondary mb-8">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => window.history?.back()}
            className="btn-outline px-6 py-2.5"
          >
            Go Back
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="btn-primary px-6 py-2.5 inline-flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Demo Page
const ErrorBoundarySystem = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ message: '', type: 'error' });
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const showNotification = (message, type = 'error') => {
    setToastConfig({ message, type });
    setShowToast(true);
  };

  const mockTechnicalError = {
    errorCode: 'ERR_NETWORK_500',
    timestamp: new Date()?.toISOString(),
    url: '/api/data',
    method: 'GET',
    statusCode: 500,
    stack: 'Error: Internal Server Error\n    at handleRequest (service.js:45)\n    at processRequest (api.js:123)'
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold text-text-primary">Error Boundary System</h1>
          <p className="text-sm text-text-secondary mt-1">
            Comprehensive error handling with user-friendly recovery options
          </p>
        </div>
      </header>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* General Error States */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary">General Error States</h2>
            
            <ErrorState
              message="Failed to load data"
              onRetry={() => showNotification('Retrying...', 'warning')}
            />
            
            <ErrorState
              message="Something went wrong with your request"
              onRetry={() => showNotification('Request retried', 'success')}
              showDetails
              technicalDetails={mockTechnicalError}
            />
          </section>

          {/* Network Errors */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary">Network Error States</h2>
            <NetworkErrorState
              isOffline={!isOnline}
              onRetry={() => showNotification('Checking connection...', 'warning')}
            />
          </section>

          {/* Permission Errors */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary">Permission Error States</h2>
            <PermissionErrorState
              requiredRole="Super Administrator"
              contactEmail="support@mervo.com"
            />
          </section>

          {/* Timeout Errors */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary">Timeout Error States</h2>
            <TimeoutErrorState
              onRetry={() => showNotification('Retrying request...', 'warning')}
              onExtendWait={() => showNotification('Waiting longer...', 'warning')}
            />
          </section>

          {/* Empty States */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Empty States</h2>
            <EmptyState
              title="No items found"
              description="Try adjusting your filters or create a new item to get started"
              action={
                <button className="btn-primary px-5 py-2 text-sm">
                  Create New Item
                </button>
              }
            />
          </section>

          {/* Toast Notifications Demo */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Toast Notifications</h2>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => showNotification('An error occurred while processing your request', 'error')}
                className="btn-primary px-4 py-2 text-sm"
              >
                Show Error Toast
              </button>
              <button
                onClick={() => showNotification('Operation completed successfully', 'success')}
                className="btn-secondary px-4 py-2 text-sm"
              >
                Show Success Toast
              </button>
              <button
                onClick={() => showNotification('Please review your input before continuing', 'warning')}
                className="btn-outline px-4 py-2 text-sm"
              >
                Show Warning Toast
              </button>
            </div>
          </section>

          {/* Accessibility Features */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Accessibility Features</h2>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                <span>Proper ARIA labels and roles on all error components</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                <span>Keyboard navigation support for all interactive elements</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                <span>Screen reader announcements for error states and toasts</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                <span>Error copying functionality for technical support</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                <span>Graceful degradation for critical functions</span>
              </li>
            </ul>
          </section>

          {/* 404 Error Preview */}
          <section className="card p-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">404 Error Page Preview</h2>
            <p className="text-sm text-text-secondary mb-4">
              Below is how the 404 error page appears when users navigate to a non-existent route.
            </p>
            <div className="border-2 border-border rounded-sm overflow-hidden">
              <div className="bg-background p-8">
                <NotFoundError />
              </div>
            </div>
          </section>
        </div>
      </main>
      {/* Toast Notification */}
      {showToast && (
        <Toast
          message={toastConfig?.message}
          type={toastConfig?.type}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default ErrorBoundarySystem;